import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { MeResponse, StartTrainingResponse } from "../types/api";
import { UnitCatalogEntry } from "../types/army";
import { castleKeys } from "./castle";

export const armyKeys = {
  me: ["me"] as const,
  units: ["catalog", "units"] as const,
};

export function useMe() {
  return useQuery({
    queryKey: armyKeys.me,
    queryFn: async () => {
      const res = await api.get<MeResponse>("/me");
      return res.data;
    },
    staleTime: 60_000,
  });
}

export function useUnitCatalog() {
  return useQuery({
    queryKey: armyKeys.units,
    queryFn: async () => {
      const res = await api.get<UnitCatalogEntry[]>("/catalog/units");
      return res.data;
    },
    staleTime: Infinity,
  });
}

export function useStartTraining() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      unitId,
      count,
      tier,
    }: {
      unitId: string;
      count: number;
      tier?: number;
    }) => {
      const res = await api.post<StartTrainingResponse>("/training/start", {
        unitId,
        count,
        ...(tier && tier > 1 ? { tier } : {}),
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: castleKeys.state });
    },
  });
}

// Free admin speedup for the active training queue. Same pattern as the
// building-side useSpeedup — takes raw seconds, no inventory item required.
// Used for test-world tuning; kept off in production via a feature flag if
// we ever decide to lock it down.
export function useTrainingAdminSpeedup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ buildingType, seconds }: { buildingType: number; seconds: number }) => {
      const res = await api.post("/training/speedup", {
        building: buildingType,
        seconds,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: castleKeys.state });
    },
  });
}

// Manual heal: moves N wounded units of `unitId` from HospitalPool into the
// active healing queue, charging 30% of training cost.
export function useStartHeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      unitId,
      count,
      tier,
    }: {
      unitId: string;
      count: number;
      tier?: number;
    }) => {
      const res = await api.post("/hospital/start-heal", {
        unitId,
        count,
        ...(tier && tier > 1 ? { tier } : {}),
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: castleKeys.state });
    },
  });
}

// Manual revive: moves N dead units of `unitId` from TemplePool into the
// active resurrection queue, charging 70% of training cost.
export function useStartRevive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      unitId,
      count,
      tier,
    }: {
      unitId: string;
      count: number;
      tier?: number;
    }) => {
      const res = await api.post("/temple/start-revive", {
        unitId,
        count,
        ...(tier && tier > 1 ? { tier } : {}),
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: castleKeys.state });
    },
  });
}

// Promote: upgrades N already-trained units of `unitId` from one tier to a
// higher one. Charges only the tier delta (cost/time difference). Units leave
// garrison[unit@fromTier] immediately and land in garrison[unit@toTier] when
// the order finishes. Mirrors POST /api/promotion/start.
export function useStartPromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      unitId,
      fromTier,
      toTier,
      count,
    }: {
      unitId: string;
      fromTier: number;
      toTier: number;
      count: number;
    }) => {
      const res = await api.post("/promotion/start", {
        unitId,
        fromTier,
        toTier,
        count,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: castleKeys.state });
    },
  });
}

// Cost ratios mirror WarService.HospitalHealCostRatio / TempleReviveCostRatio.
// Used client-side for the cost preview before submitting.
export const HEAL_COST_RATIO = 0.30;
export const REVIVE_COST_RATIO = 0.70;
